import { Button } from '@/components/ui/button';
import { BREAKPOINTS } from '@/constants';
import { useMatchMedia } from '@/hooks/useMatchMedia';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef } from 'react';

export default function Hero() {
  const refs = {
    hero: useRef(null),
    titleLines: useRef(null),
    textLines: useRef(null),
    button: useRef(null),
    image: useRef(null),
  };

  const isMobile = useMatchMedia(BREAKPOINTS.MD);

  useGSAP(
    () => {
      if (!refs.hero.current) return;

      const splitTitle = new SplitText(refs.titleLines.current, {
        type: 'lines',
        mask: 'lines',
      });
      const splitText = new SplitText(refs.textLines.current, {
        type: 'lines',
        mask: 'lines',
      });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
      });

      gsap.set(splitTitle.lines, { yPercent: MOTION.yPercent });
      gsap.set(splitText.lines, { y: MOTION.y });
      gsap.set(refs.button.current, { autoAlpha: 0 });
      gsap.set(refs.image.current, { autoAlpha: 0, y: MOTION.y });

      if (isMobile) {
        tl.to(refs.image.current, { autoAlpha: 1, y: 0, duration: MOTION.durationLg })
          .to(
            splitTitle.lines,
            {
              yPercent: 0,
              stagger: MOTION.staggerLines,
            },
            '<+0.2',
          )
          .fromTo(refs.button.current, { autoAlpha: 0 }, { autoAlpha: 1 }, '<+0.2')
          .to(splitText.lines, { y: 0, stagger: MOTION.staggerLines }, '<+0.2');
      } else {
        tl.to(splitTitle.lines, {
          yPercent: 0,
          stagger: MOTION.staggerLines,
        })
          .to(refs.image.current, { autoAlpha: 1, y: 0, duration: MOTION.durationLg }, '<+0.2')
          .to(refs.button.current, { autoAlpha: 1 }, '<')
          .to(splitText.lines, { y: 0, stagger: MOTION.staggerLines }, '<+0.2');
      }
    },
    { scope: refs.hero, dependencies: [isMobile] },
  );

  return (
    <section
      ref={refs.hero}
      className="max-width-blue-section mx-auto grid grid-cols-1 items-center gap-5 px-6 py-16 md:px-8 lg:grid-cols-2"
    >
      <div className="row-start-2 flex flex-col items-center space-y-10 md:px-4 lg:row-start-1 lg:items-start">
        <h1 ref={refs.titleLines} className="text-center font-medium lg:text-left">
          <span className="text-orange">zLawyer</span>, Le logiciel de gestion et facturation pour
          cabinet d&apos;avocats
          <span className="text-orange block">simple,</span>
          <span className="text-orange block">ergonomique,</span>
          <span className="text-orange block">sans engagement&nbsp;!</span>
        </h1>

        <Button ref={refs.button} href="/contact" variant="fill">
          Je souhaite une démonstration
        </Button>
      </div>

      <div className="relative flex flex-col items-center">
        <div className="transform-gpu perspective-[1500px]">
          <Image
            ref={refs.image}
            alt="illustration du logiciel"
            className="invisible w-full transform-gpu lg:rotate-y-[-15deg]"
            decoding="async"
            height={648}
            priority={true}
            sizes="(min-width: 1024px) 50vw, 100vw"
            src={withBasePath('/images/hero/illustrationHero.webp')}
            width={939}
          />
        </div>
        <p className="sr-only">
          Expérimentez le logiciel de facturation le plus intuitif et performant du marché&nbsp;!
        </p>

        <p
          aria-hidden="true"
          className="mt-4 hidden max-w-md text-center text-lg text-slate-800 lg:block"
        >
          <span ref={refs.textLines} className="block">
            Expérimentez le logiciel de facturation le plus intuitif et performant du marché&nbsp;!
          </span>
        </p>
      </div>
    </section>
  );
}
